<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Container for HrTabPanel content. Pairs with HrTabs via a shared v-model.
 *
 * v-model flows through attrs.
 *
 * @status recommended
 * @group navigation
 * @useWhen Wrapping HrTabPanel components to show/hide the active panel based on a shared v-model
 * @avoidWhen No tab bar offers the panels to the user — use HrPanels, which does not announce as tabs
 * @related HrTabs
 * @related HrPanels
 * @see https://quasar.dev/vue-components/tab-panels Quasar q-tab-panels API
 */
defineOptions({ name: 'HrTabPanels' });

const props = defineProps({
  /**
   * Keeps inactive panels mounted (preserves their state).
   */
  keepAlive: {
    type: Boolean,
    default: false,
  },
  /**
   * Set when the panels sit inside a container that already scrolls, such as a dialog's content
   * area. The panel then sizes to what it is showing instead of owning its own scrolling, so the
   * container is the only scroller and short panels are not scrolled inside a taller box.
   */
  contentSized: {
    type: Boolean,
    default: false,
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => ({
  animated: true,
  keepAlive: props.keepAlive,
  ...attrs,
}));
</script>

<template>
  <q-tab-panels
    v-bind="effectiveProps"
    :class="[hasTheme ? 'hr-tab-panels' : undefined,
             contentSized ? 'hr-tab-panels--content-sized' : undefined]"
  >
    <!-- @slot HrTabPanel components. -->
    <slot />
  </q-tab-panels>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-tab-panels {
  background: transparent;
}

/*
 * Quasar renders each panel as `q-panel scroll`, which is an overflow: auto box, and forces the
 * panel's child to height: 100%. Nested in a container that already scrolls that gives two
 * scrollers for one panel, and the percentage height leaves a short panel scrolling inside a box
 * sized by something other than its own content.
 *
 * Both are undone rather than one: the height alone still leaves the auto overflow, and the
 * overflow alone still leaves the panel unable to grow. `.q-panel-parent` keeps its own
 * overflow: hidden deliberately, since that is what clips the outgoing panel during the slide.
 */
.hr-tab-panels--content-sized {
  :deep(.q-panel) {
    overflow: visible;
  }

  :deep(.q-panel > div) {
    height: auto;
  }
}

</style>
